import { useEffect, useRef, useState } from "react";
import { travelDays, type TravelDay, type TravelSchedule } from "../core/travel-schedule";

export function useTravelSchedule(deviceId: string) {
	const [data, setData] = useState<TravelSchedule | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [refreshing, setRefreshing] = useState(false);
	const [revision, setRevision] = useState(0);
	const busy = useRef(false);
	const generation = useRef(0);
	useEffect(() => {
		let disposed = false;
		const load = async () => {
			if (busy.current) return;
			const current = ++generation.current;
			setRefreshing(true);
			const feedback = new Promise<void>(resolve => window.setTimeout(resolve, 600));
			try {
				const response = await fetch(`/api/travel-schedule?deviceId=${encodeURIComponent(deviceId)}`);
				if (!response.ok) throw new Error("Shared schedule unavailable");
				const body = await response.json() as { schedule: TravelSchedule };
				if (!travelDays(body.schedule?.days)) throw new Error("Invalid shared schedule");
				if (!disposed && current === generation.current) { setData(body.schedule); setError(null); }
			} catch {
				if (!disposed && current === generation.current) setError("Shared schedule unavailable. Refresh to try again.");
			} finally {
				await feedback;
				if (!disposed && current === generation.current) setRefreshing(false);
			}
		};
		void load();
		const visible = () => { if (document.visibilityState === "visible") void load(); };
		const timer = window.setInterval(visible, 30_000);
		document.addEventListener("visibilitychange", visible);
		return () => { disposed = true; ++generation.current; window.clearInterval(timer); document.removeEventListener("visibilitychange", visible); };
	}, [deviceId, revision]);
	async function save(days: TravelDay[], version: number) {
		if (busy.current) return false;
		busy.current = true;
		setSaving(true);
		setRefreshing(false);
		const current = ++generation.current;
		try {
			const response = await fetch("/api/travel-schedule", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ deviceId, days, version }) });
			if (!response.ok && response.status !== 409) throw new Error("Save failed");
			const body = await response.json() as { schedule: TravelSchedule };
			if (current !== generation.current) return false;
			setData(body.schedule);
			setError(response.status === 409 ? "Schedule changed. Review it before saving again." : null);
			return response.ok;
		} catch {
			if (current === generation.current) setError("Schedule not saved. Refresh before trying again.");
			return false;
		} finally { busy.current = false; setSaving(false); }
	}
	return { data, error, saving, refreshing, save, refresh: () => setRevision(value => value + 1) };
}